import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { EmailCampaign } from '@/server/modules/email/db/schema'
import type {
  CreateCampaignInput,
  UpdateCampaignInput,
  CampaignsQuery,
  RecipientFilters,
  TestEmailInput,
  PreviewCampaignInput,
  ScheduleCampaignInput,
} from '@/shared/schemas/email.schema'
import { buildQueryString, fetchJson, fetchMutation } from '@/client/lib/api-helpers'

/**
 * TanStack Query hooks for email campaigns
 *
 * Provides data fetching and mutations with automatic cache management
 * Supports search, filtering, and pagination
 */

// Base URL for API requests (relative to current origin)
const CAMPAIGNS_API = '/api/email/campaigns'
const RECIPIENTS_API = '/api/email/recipients'

// Query keys for cache management
const CAMPAIGNS_KEYS = {
  all: ['email-campaigns'] as const,
  lists: () => [...CAMPAIGNS_KEYS.all, 'list'] as const,
  list: (params?: CampaignsQuery) => [...CAMPAIGNS_KEYS.lists(), params] as const,
  details: () => [...CAMPAIGNS_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...CAMPAIGNS_KEYS.details(), id] as const,
  preview: (id: string) => [...CAMPAIGNS_KEYS.all, 'preview', id] as const,
}

const RECIPIENTS_KEYS = {
  all: ['email-recipients'] as const,
  count: (filters?: RecipientFilters) => [...RECIPIENTS_KEYS.all, 'count', filters] as const,
  list: (filters?: RecipientFilters) => [...RECIPIENTS_KEYS.all, 'list', filters] as const,
}

// API response types
interface CampaignsResponse {
  campaigns: EmailCampaign[]
  count: number
  total: number
}

interface PreviewResponse {
  campaign: EmailCampaign
  sampleContact: {
    id: string
    firstName: string | null
    lastName: string | null
    email: string
  } | null
  recipientCount: number
  previewHtml: string
  previewSubject: string
}

interface SendResponse {
  campaign: EmailCampaign
  result: {
    totalRecipients: number
    sentCount: number
    failedCount: number
  }
  message: string
}

interface TestEmailResponse {
  success: boolean
  message: string
  recipient: string
  messageId?: string
}

interface RecipientCountResponse {
  count: number
}

interface RecipientListResponse {
  recipients: {
    id: string
    firstName: string | null
    lastName: string | null
    email: string
    companyId: string | null
    tags: string | null
    status: string
  }[]
  count: number
  total: number
}

// ============ Campaign Hooks ============

/**
 * Fetch all campaigns for the current user
 * Supports search, filtering, and pagination
 */
export function useEmailCampaigns(params?: CampaignsQuery) {
  return useQuery({
    queryKey: CAMPAIGNS_KEYS.list(params),
    queryFn: async (): Promise<EmailCampaign[]> => {
      const data = await fetchJson<CampaignsResponse>(
        `${CAMPAIGNS_API}${buildQueryString(params)}`
      )
      return data.campaigns
    },
  })
}

/**
 * Fetch a single campaign by ID
 */
export function useEmailCampaign(id: string) {
  return useQuery({
    queryKey: CAMPAIGNS_KEYS.detail(id),
    queryFn: async (): Promise<EmailCampaign> => {
      const data = await fetchJson<{ campaign: EmailCampaign }>(`${CAMPAIGNS_API}/${id}`)
      return data.campaign
    },
    enabled: !!id,
  })
}

/**
 * Create a new campaign
 */
export function useCreateEmailCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateCampaignInput): Promise<EmailCampaign> => {
      const data = await fetchMutation<{ campaign: EmailCampaign }>(CAMPAIGNS_API, 'POST', input)
      return data.campaign
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAMPAIGNS_KEYS.lists() })
    },
  })
}

/**
 * Update an existing campaign
 */
export function useUpdateEmailCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: UpdateCampaignInput
    }): Promise<EmailCampaign> => {
      const result = await fetchMutation<{ campaign: EmailCampaign }>(
        `${CAMPAIGNS_API}/${id}`,
        'PUT',
        data
      )
      return result.campaign
    },
    onSuccess: (campaign) => {
      queryClient.invalidateQueries({ queryKey: CAMPAIGNS_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: CAMPAIGNS_KEYS.detail(campaign.id) })
    },
  })
}

/**
 * Delete a campaign
 */
export function useDeleteEmailCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await fetchMutation<{ success: boolean }>(`${CAMPAIGNS_API}/${id}`, 'DELETE')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAMPAIGNS_KEYS.lists() })
    },
  })
}

/**
 * Preview a campaign with interpolated variables
 *
 * Uses POST because preview generation may require input parameters
 * and the operation generates a fresh preview (not cached server-side)
 */
export function useCampaignPreview(id: string, input?: PreviewCampaignInput) {
  return useQuery({
    queryKey: CAMPAIGNS_KEYS.preview(id),
    queryFn: async (): Promise<PreviewResponse> => {
      return fetchMutation<PreviewResponse>(`${CAMPAIGNS_API}/${id}/preview`, 'POST', input || {})
    },
    enabled: !!id,
  })
}

/**
 * Send campaign immediately
 */
export function useSendCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<SendResponse> => {
      return fetchMutation<SendResponse>(`${CAMPAIGNS_API}/${id}/send`, 'POST')
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: CAMPAIGNS_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: CAMPAIGNS_KEYS.detail(data.campaign.id) })
    },
  })
}

/**
 * Schedule a campaign for later
 */
export function useScheduleCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: ScheduleCampaignInput
    }): Promise<EmailCampaign> => {
      const result = await fetchMutation<{ campaign: EmailCampaign }>(
        `${CAMPAIGNS_API}/${id}/schedule`,
        'POST',
        data
      )
      return result.campaign
    },
    onSuccess: (campaign) => {
      queryClient.invalidateQueries({ queryKey: CAMPAIGNS_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: CAMPAIGNS_KEYS.detail(campaign.id) })
    },
  })
}

/**
 * Cancel a scheduled campaign
 */
export function useCancelCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<EmailCampaign> => {
      const result = await fetchMutation<{ campaign: EmailCampaign }>(
        `${CAMPAIGNS_API}/${id}/cancel`,
        'POST'
      )
      return result.campaign
    },
    onSuccess: (campaign) => {
      queryClient.invalidateQueries({ queryKey: CAMPAIGNS_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: CAMPAIGNS_KEYS.detail(campaign.id) })
    },
  })
}

/**
 * Send a test email
 */
export function useSendTestEmail() {
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data?: TestEmailInput
    }): Promise<TestEmailResponse> => {
      return fetchMutation<TestEmailResponse>(`${CAMPAIGNS_API}/${id}/test`, 'POST', data || {})
    },
  })
}

// ============ Recipients Hooks ============
// Note: These use POST requests because RecipientFilters contains complex nested
// objects (arrays, conditions) that don't serialize well to URL query parameters.
// The queries are still idempotent - POST is used purely for request body support.

/**
 * Count recipients matching the given filters
 *
 * Uses POST to support complex filter objects in request body
 */
export function useRecipientCount(filters?: RecipientFilters) {
  return useQuery({
    queryKey: RECIPIENTS_KEYS.count(filters),
    queryFn: async (): Promise<number> => {
      const data = await fetchMutation<RecipientCountResponse>(
        `${RECIPIENTS_API}/count`,
        'POST',
        { filters }
      )
      return data.count
    },
  })
}

/**
 * List recipients matching the given filters
 *
 * Uses POST to support complex filter objects in request body
 */
export function useRecipientList(
  filters?: RecipientFilters,
  options?: { limit?: number; offset?: number; enabled?: boolean }
) {
  return useQuery({
    queryKey: RECIPIENTS_KEYS.list(filters),
    queryFn: async (): Promise<RecipientListResponse> => {
      return fetchMutation<RecipientListResponse>(`${RECIPIENTS_API}/list`, 'POST', {
        filters,
        limit: options?.limit || 50,
        offset: options?.offset || 0,
      })
    },
    enabled: options?.enabled !== false,
  })
}
